import './RoleStageActionFlows.scss';
import ConsentActions from 'components/molecules/ConsentActions/ConsentActions';
import Container from 'components/molecules/Container/Container';
import NavbarWithBackLink from 'components/molecules/NavbarWithBackLink/NavbarWithBackLink';
import { HelmetAndPageAnnouncer } from 'components/organisms/HelmetAndPageAnnouncer/HelmetAndPageAnnouncer';
import TestIntro from 'components/organisms/TestIntro/TestIntro';
import { appSwitchRoutes } from 'constants/routes';
import {
  SupportedLocale,
  TestStatus,
  useDeiStageActionCandidateDeclineConsentMutation,
  useDeiStageActionCandidateGiveConsentMutation,
  useUserQuery,
} from 'generated/graphql';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Loader,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import config from 'views/Test/TestInfo/TestInfo.constants';
import { useRoleStageActionsFlow } from './useRoleStageActionsFlow';

const RoleStageDeiIntro: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { roleId, stageId, role, stage, loading, exiting, actionsUrl } =
    useRoleStageActionsFlow();

  const { handleMsgType } = useNotification();

  const onError = (error: { message?: string }) =>
    handleMsgType({ type: TNotification.error, message: error?.message });

  const [giveConsent, { loading: givingConsent }] =
    useDeiStageActionCandidateGiveConsentMutation({
      onCompleted: () =>
        navigate(appSwitchRoutes.roleStageDeiForm.getUrl(roleId, stageId)),
      onError,
    });
  const [declineConsent, { loading: decliningConsent }] =
    useDeiStageActionCandidateDeclineConsentMutation({
      onCompleted: () => navigate(actionsUrl),
      onError,
    });

  const userQueryResponse = useUserQuery();
  const assessmentLocale =
    userQueryResponse.data?.User.preferredLocale.locale ||
    SupportedLocale.English;

  const action = stage?.actions.deiAction;

  // The stage has no DEI action — nothing to fill in here.
  useEffect(() => {
    if (stage && !action) {
      navigate(actionsUrl);
    }
  }, [stage, action, navigate, actionsUrl]);

  if (loading) {
    return (
      <Container className="page-loader-wrapper">
        <Loader variant="bubbles" />
      </Container>
    );
  }

  if (exiting || !role || !stage || !action) {
    return null;
  }

  const singleTestConfig = config['dei']({
    companyName: role.company.name,
  });
  const MainIcon = singleTestConfig.icon;

  return (
    <Container className="TestInfoContainer">
      <NavbarWithBackLink
        to={actionsUrl}
        ariaLabel={t('assessment.returnToAllAssessmentActions')}
        label={t('assessment.actions')}
      />
      <HelmetAndPageAnnouncer pageTitle={singleTestConfig.title} />
      <TestIntro
        MainIcon={MainIcon}
        type={singleTestConfig.key}
        assessmentLocale={assessmentLocale}
        title={singleTestConfig.title}
        notes={singleTestConfig.notes}
        testStatus={TestStatus.NotStarted}
        hasPractice={false}
        onStart={() => {}}
        onStartPracticeTest={() => {}}
        testTimePerQuestion={2}
        customActions={
          <ConsentActions
            idPrefix="dei-consent"
            loading={givingConsent || decliningConsent}
            checkboxLabel={t('test.instructions.dei.consentLabel')}
            errorText={t('test.instructions.dei.consentError')}
            declineLabel={t('test.instructions.dei.decline')}
            confirmLabel={t('test.instructions.dei.startNow')}
            onConfirm={() =>
              giveConsent({
                variables: { stageCandidateId: stage.stageCandidateId },
              })
            }
            onDecline={() =>
              declineConsent({
                variables: { stageCandidateId: stage.stageCandidateId },
              })
            }
          />
        }
      />
    </Container>
  );
};

export default RoleStageDeiIntro;
